import { cn } from "@/lib/utils";

/**
 * THE TREND LINE.
 *
 * The second instrument, next to the sight glass. Where the glass reads "how much, right
 * now", this reads "which way, and how fast" — the shape an operator's eye goes to when the
 * question is "is this getting better or worse". No package for it: the product has no
 * charting library, and a sparkline is a handful of SVG path commands, not a dependency.
 *
 * No gradient fill — DESIGN.md §7 is explicit that this product has none, on anything. The
 * area under the line is the same instrument colour at low opacity, nothing more.
 *
 * The chart is decorative (`aria-hidden`): the numbers it plots are also written in `.num`
 * text around it, and that text is what a screen reader and a copy-paste both need. A
 * caller must always pair this with a real, readable min/max/latest caption.
 */

export type ChartTone = "ok" | "low" | "dry" | "idle";

const TONE_CLASS: Record<ChartTone, string> = {
  ok: "text-ok",
  low: "text-low",
  dry: "text-dry",
  idle: "text-mauve",
};

export interface TrendChartProps {
  /**
   * Chronological, oldest first — exactly what the spec's readings endpoints return.
   *
   * `y: null` is a MISSING reading, and it is drawn as a gap in the line, never as a zero.
   * A controller that stopped answering for four minutes did not report 0 V; plotting it at
   * the axis would draw a cliff an operator reads as a power cut that never happened.
   */
  points: { x: number; y: number | null }[];
  tone?: ChartTone;
  height?: number;
  className?: string;
}

/**
 * A time series always reads left-to-right, oldest to newest, in EITHER language — that is
 * a convention of the instrument, not of the sentence around it. The caller wraps this in a
 * `dir="ltr"` container for exactly that reason, the same way `.num` isolates a figure.
 */
export function TrendChart({ points, tone = "ok", height = 96, className }: TrendChartProps) {
  if (points.length === 0) return null;

  const width = 320;
  const pad = 4;

  // A run of consecutive readings that all carried a value. Each run is stroked on its own,
  // so the space where a value was missing stays empty instead of being bridged.
  const runs: { x: number; y: number }[][] = [];
  for (const point of points) {
    if (point.y === null || !Number.isFinite(point.y)) {
      if (runs[runs.length - 1]?.length) runs.push([]);
      continue;
    }
    if (runs.length === 0) runs.push([]);
    runs[runs.length - 1]!.push({ x: point.x, y: point.y });
  }
  const drawn = runs.filter((run) => run.length > 0);
  if (drawn.length === 0) return null;

  // The axes span the whole WINDOW, gaps included — a chart whose x-axis quietly skipped the
  // silent minutes would show a break of the wrong width, which is the same lie again.
  const xs = points.map((p) => p.x);
  const ys = drawn.flat().map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const spanX = maxX - minX || 1;
  // A flat line (one point, or a value that never moved) still deserves a visible stroke
  // across the middle of the chart rather than collapsing to the bottom edge.
  const spanY = maxY - minY || 1;

  const scaleX = (x: number) => pad + ((x - minX) / spanX) * (width - pad * 2);
  const scaleY = (y: number) =>
    height - pad - ((y - minY) / spanY) * (height - pad * 2);

  const runPath = (run: { x: number; y: number }[]) =>
    run
      .map((p, i) => `${i === 0 ? "M" : "L"} ${scaleX(p.x).toFixed(1)} ${scaleY(p.y).toFixed(1)}`)
      .join(" ");

  const linePath = drawn.map(runPath).join(" ");

  const areaPath = drawn
    .filter((run) => run.length >= 2)
    .map((run) => {
      const first = run[0]!;
      const last = run[run.length - 1]!;
      return (
        `${runPath(run)} L ${scaleX(last.x).toFixed(1)} ${(height - pad).toFixed(1)} ` +
        `L ${scaleX(first.x).toFixed(1)} ${(height - pad).toFixed(1)} Z`
      );
    })
    .join(" ");

  // A reading with a gap on either side is a dot: a one-point path strokes nothing.
  const isolated = drawn.filter((run) => run.length === 1).map((run) => run[0]!);
  const newest = drawn[drawn.length - 1]!.at(-1)!;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      className={cn("h-full w-full", TONE_CLASS[tone], className)}
    >
      {areaPath ? <path d={areaPath} fill="currentColor" fillOpacity={0.15} stroke="none" /> : null}
      <path
        d={linePath}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {isolated.map((p) => (
        <circle key={p.x} cx={scaleX(p.x)} cy={scaleY(p.y)} r={1.75} fill="currentColor" />
      ))}
      {/* The most recent point, lit — the "this is happening now" mark at the live edge. */}
      <circle cx={scaleX(newest.x)} cy={scaleY(newest.y)} r={2.5} fill="currentColor" />
    </svg>
  );
}
